//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import TextColor from 'common/components/core/colors/text.scss';
import BackgroundColor from 'common/components/core/colors/background.scss';
import theme from './CustomIcon.scss';
import Tooltip from 'common/components/core/tooltip';
import PlaceholderLoader from 'common/components/core/loader/PlaceholderLoader';

export const IconTypes = {
    Default: 'DEFAULT',
    Solid: 'SOLID'
};

type IconParams = {
    icon: string | any,
    theme: Object,
    style?: { [string]: string },
    invert?: boolean,
    iconColor?: string,
    circular?: boolean,
    solid?: boolean,
    bgColor?: string,
    wrapBgColor?: string,
    center?: boolean,
    numberLabel?: ?string,
    className?: string | any,
    onClick?: () => void,
    onMouseEnter?: () => void,
    onMouseLeave?: () => void,
    onMouseMove?: () => void,
    onMouseDown?: () => void,
    onTouchEnd?: () => void,
    dataTestId?: string,
    disabled?: boolean
};

type HighlightIconParams = {
    icon: string | any,
    theme: Object,
    invert?: boolean,
    solid?: boolean,
    invertHighlight?: boolean,
    bgColor?: string,
    iconColor?: string,
    circular?: boolean,
    highlightIcon?: string,
    highlightBg?: string,
    highlightWrapBgColor?: string,
    highlightColor: string,
    numberLabel?: ?string
};
const Icon = ({
    icon,
    theme,
    invert,
    solid,
    bgColor,
    iconColor,
    circular,
    center,
    style,
    numberLabel,
    className,
    wrapBgColor,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onMouseMove,
    onMouseDown,
    onTouchEnd,
    'data-test-id': dataTestId,
    disabled
}: IconParams) => {
    let isIcon = false;
    if (icon) isIcon = true;
    const bgColorClass = BackgroundColor[bgColor];
    const iconColorClass = TextColor[iconColor];
    let bgStyle;
    if (Array.isArray(bgColor)) {
        bgStyle = {
            background: `linear-gradient(135deg, ${bgColor[0]} 40%, ${bgColor[1]} 60%)`
        };
    }
    return (
        <div
            className={classnames(theme.iconWrap, className, {
                [theme.solid]: solid,
                [theme.circular]: circular,
                [theme.center]: center,
                [BackgroundColor[wrapBgColor]]: wrapBgColor
            })}
            {...(!disabled
                ? {
                      onClick,
                      onMouseEnter,
                      onMouseMove,
                      onMouseDown,
                      onTouchEnd
                  }
                : null)}
            onMouseLeave={onMouseLeave}
            style={style}
            data-test-id={dataTestId}
        >
            <span
                data-toolbox="icon"
                className={classnames(theme.icon, {
                    'ml-icon': isIcon,
                    [icon]: isIcon,
                    [theme.isIcon]: isIcon && theme.isIcon,
                    [theme.invert]: invert,
                    [bgColorClass]: bgColor ? true : false,
                    [iconColorClass]: iconColor ? true : false
                })}
                style={bgStyle}
            >
                {numberLabel}
            </span>
        </div>
    );
};

const HighlightIcon = ({
    icon,
    theme,
    invert,
    invertHighlight,
    bgColor,
    iconColor,
    circular,
    highlightIcon,
    highlightBg,
    highlightWrapBgColor,
    highlightColor,
    numberLabel,
    ...others
}: HighlightIconParams) => {
    return (
        <div className={theme.iconWithHighlightWrap} {...others}>
            <Icon
                theme={theme}
                invert={invert}
                icon={icon}
                circular={circular}
                iconColor={iconColor}
                numberLabel={numberLabel}
                bgColor={bgColor}
            />
            {highlightIcon ? (
                <Icon
                    theme={theme}
                    className={theme.highlightIconWrap}
                    invert={invertHighlight}
                    icon={highlightIcon}
                    circular={circular}
                    iconColor={highlightColor}
                    bgColor={highlightBg}
                    wrapBgColor={highlightWrapBgColor}
                />
            ) : null}
        </div>
    );
};

Icon.defaultProps = {
    circular: false
};

const ThemedHighlightIcon = themr('CustomIcon', theme)(HighlightIcon);
const ThemedIcon = themr('CustomIcon', theme)(Icon);

const ThemedIconLoader = PlaceholderLoader(ThemedIcon);
const TooltipThemedIconLoader = Tooltip(ThemedIconLoader);

export {
    ThemedIcon as Icon,
    ThemedHighlightIcon as HighlightIcon,
    TooltipThemedIconLoader as TooltipLoaderIcon
};
export default ThemedIcon;
